import {
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  runTransaction,
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';
import { db } from './config';

// Single collection for all features
const FEATURES_COLLECTION = 'features';

// Get all features
export const getAllFeatures = async () => {
  try {
    const features = {};
    const featuresRef = collection(db, FEATURES_COLLECTION);
    const snapshot = await getDocs(featuresRef);
    
    snapshot.forEach((doc) => {
      features[doc.id] = {
        id: doc.id,
        ...doc.data()
      };
    });

    return { success: true, data: features };
  } catch (error) {
    console.error('Error getting features:', error);
    return { success: false, error: error.message };
  }
};

// Get only active features
export const getActiveFeatures = async () => {
  try {
    const features = [];
    const featuresRef = collection(db, FEATURES_COLLECTION);
    const activeQuery = query(featuresRef, where('active', '!=', false));
    const snapshot = await getDocs(activeQuery);
    
    snapshot.forEach((doc) => {
      features.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: features };
  } catch (error) {
    console.error('Error getting active features:', error);
    return { success: false, error: error.message };
  }
};

// Helper function to convert a string to snake_case
const toSnakeCase = (str) => {
  if (!str) return '';
  return String(str)
    .trim()
    .replace(/([A-Z])/g, '_$1') // Add underscore before capitals
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-zA-Z0-9_]+/g, '_') // Replace non-alphanumeric (excluding underscore) with underscore
    .toLowerCase()
    .replace(/^_+|_+$/g, ''); // Trim leading/trailing underscores
};

// Add a new feature (auto-detected or user-created)
export const addFeature = async (featureData) => {
  try {
    if (!featureData || !featureData.label || !featureData.category) {
      return { success: false, error: 'Feature label and category are required.' };
    }

    const standardizedLabel = featureData.label.trim();
    const featureId = toSnakeCase(standardizedLabel);

    if (!featureId) {
      return { success: false, error: 'Invalid feature name format, results in empty ID.' };
    }

    const featureRef = doc(db, FEATURES_COLLECTION, featureId);
    
    await runTransaction(db, async (transaction) => {
      const featureSnap = await transaction.get(featureRef);
      
      if (featureSnap.exists()) {
        const existingData = featureSnap.data();
        const updates = {};

        // If feature exists, update its source if it's auto-detected and new data is manual
        if (existingData.source === 'auto' && featureData.source === 'manual') {
          updates.source = 'manual';
          updates.active = true; // Ensure it's active if manually confirmed
        }

        // Update category if provided and different
        if (featureData.category && existingData.category !== featureData.category) {
          updates.category = featureData.category;
        }
        
        // Update label if provided and different (but ID remains the same)
        if (standardizedLabel !== existingData.label) {
            updates.label = standardizedLabel;
        }

        // If there are any updates, apply them
        if (Object.keys(updates).length > 0) {
          updates.updatedAt = serverTimestamp();
          transaction.update(featureRef, updates);
        }
      } else {
        // Create new feature
        transaction.set(featureRef, {
          id: featureId, // Store the standardized snake_case ID
          label: standardizedLabel,
          source: featureData.source || 'manual', // Default to manual
          category: featureData.category,
          active: true, // New features are active by default
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    });

    return { success: true, data: { id: featureId, label: standardizedLabel, category: featureData.category } };
  } catch (error) {
    console.error('Error adding feature:', error);
    return { success: false, error: error.message };
  }
};

// Get or create a feature from auto-detection
export const getOrCreateFeatureFromAutoDetect = async (className, confidence, category = 'uncategorized') => {
  try {
    if (!className) {
      return { success: false, error: 'Class name is required for auto-detected feature.' };
    }
    const standardizedLabel = className.trim();
    const featureId = toSnakeCase(standardizedLabel);

    if (!featureId) {
      return { success: false, error: 'Invalid class name format for auto-detected feature.' };
    }

    const featureRef = doc(db, FEATURES_COLLECTION, featureId);
    let featureDocData;
    
    await runTransaction(db, async (transaction) => {
      const featureSnap = await transaction.get(featureRef);
      
      if (!featureSnap.exists()) {
        // Create new feature if it doesn't exist
        featureDocData = {
          id: featureId,
          label: standardizedLabel,
          source: 'auto',
          category: category, // Assign a category, default to 'uncategorized'
          active: true, // Auto-detected features can start as active
          confidence: confidence || 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        transaction.set(featureRef, featureDocData);
      } else {
        // Feature exists, potentially update confidence or other details
        const existingData = featureSnap.data();
        featureDocData = { id: featureId, ...existingData };
        const updates = {};

        if (confidence && confidence > (existingData.confidence || 0)) {
          updates.confidence = confidence;
        }
        // If it was 'auto' and no category, or 'uncategorized', update with new category if different and not 'uncategorized'
        if (category !== 'uncategorized' && existingData.category !== category) {
            if (existingData.source === 'auto' && (!existingData.category || existingData.category === 'uncategorized')) {
                 updates.category = category;
            }
        }


        if (Object.keys(updates).length > 0) {
          updates.updatedAt = serverTimestamp();
          transaction.update(featureRef, updates);
          featureDocData = { ...featureDocData, ...updates }; // Reflect updates in returned data
        }
      }
    });

    return { success: true, data: featureDocData };
  } catch (error) {
    console.error('Error getting/creating auto-detected feature:', error);
    return { success: false, error: error.message };
  }
};

// Update an existing feature
export const updateFeature = async (featureId, featureData) => {
  try {
    const featureRef = doc(db, FEATURES_COLLECTION, featureId);
    
    await runTransaction(db, async (transaction) => {
      const featureSnap = await transaction.get(featureRef);
      
      if (!featureSnap.exists()) {
        throw new Error('Feature not found');
      }

      transaction.update(featureRef, {
        ...featureData,
        updatedAt: serverTimestamp()
      });
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating feature:', error);
    return { success: false, error: error.message };
  }
};

// Delete a feature
export const deleteFeature = async (featureId) => {
  try {
    const featureRef = doc(db, FEATURES_COLLECTION, featureId);
    
    await runTransaction(db, async (transaction) => {
      const featureSnap = await transaction.get(featureRef);
      
      if (!featureSnap.exists()) {
        throw new Error('Feature not found');
      }

      transaction.delete(featureRef);
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting feature:', error);
    return { success: false, error: error.message };
  }
};
